import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useTheme } from "@emotion/react";
import { Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { isEqual, omit } from "lodash";

import Stepper from "common/Stepper";
import ApplicantDetails from "Components/ApplicantDetails";
import AddressForPower from "Components/AddressForPower";
import UtilitiesFields from "pages/Consumer/OtherApplications/UtilitiesFields";
import ConnectionLoadDetails from "pages/Consumer/ApplicationType/PermanentConnection/ConnectionLoadDetails";
import UtilityConsumerDetails from "./UtilityConsumerDetails";
import { premiseNamesForTimeShift } from "common/constant";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import { getOptionLabel, getOptionValue } from "common/enums";

import {
  getOtherUtilityConsumerValidation,
  getUtilityConsumerValidation,
} from "utils/utilityConsumerValidationSchema";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { filterEmptyStrings } from "utils";

import { useApplicantTypeQuery } from "apis/ApplicantType";
import {
  useLazyConsumerByIdQuery,
  useLazyGetOtherConsumerByIdQuery,
} from "apis/User";
import { usePutUtilityConsumerMutation } from "apis/UtilityConsumer";
import { useFloorQuery } from "apis/Floor";
import { usePutUpdateOtherUtilityConsumerMutation } from "apis/UtilityConsumer/utilityConsumer";

const keysToDeleteForPowerUtility = [
  "connectionLoad",
  "electricityDuty",
  "sourceOfPowerId",
];

const keysToDeleteForOtherUtility = [
  "connectionLoad.tention",
  "connectionLoad.backupRequired",
  "connectionLoad.isGreenPowerRequired",
  "totalBuildupArea",
  "waterSewageDemand",
  "dcsDemand",
  "totalNumberOfOccupants",
];

const UtilityConsumerEditModal = ({
  utilityConsumerId,
  handleClose,
  utilityIds,
  t,
}) => {
  const [
    getUtilityConsumerDataByConsumerId,
    { data: { data: powerConsumerData } = {}, isLoading },
  ] = useLazyConsumerByIdQuery();

  const [
    getOtherUtilityConsumerDataByConsumerId,
    {
      data: { data: otherConsumerData } = {},
      isLoading: isOtherUtilityConsumerDataLoading,
    },
  ] = useLazyGetOtherConsumerByIdQuery();

  const { data: { data: floors } = {} } = useFloorQuery();

  const [addUtilityConsumer, { isLoading: isConsumerDataUpdating }] =
    usePutUtilityConsumerMutation();

  const [
    updateOtherUtilityUtilityConsumer,
    { isLoading: isOtherUtilityConsumerDataUpdating },
  ] = usePutUpdateOtherUtilityConsumerMutation();

  const { data: { data: { items: applicantTypes } = {} } = {} } =
    useApplicantTypeQuery();

  const [currentStep, setCurrentStep] = useState(0);
  const [iseRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [remarks, setRemarks] = useState("");

  const theme = useTheme();

  const floorOptions = useMemo(
    () =>
      floors?.map((floor) => ({ label: floor.name, value: floor.id })) ?? [],
    [floors]
  );

  const consumerData = useMemo(
    () => powerConsumerData ?? otherConsumerData,
    [powerConsumerData, otherConsumerData]
  );

  const initialValues = useMemo(
    () => ({
      id: consumerData?.id ?? 0,
      consumerNumber: consumerData?.consumerNumber ?? "",
      consumerName: consumerData?.consumerName ?? "",
      groupId: consumerData?.groupId ?? "",
      utilityId: utilityIds.includes(getOptionValue("utilityOptions", "Power"))
        ? 1
        : "",
      utilities: !utilityIds.includes(getOptionValue("utilityOptions", "Power"))
        ? utilityIds.map((val) => ({
            label: getOptionLabel("utilityOptions", val),
            value: val,
          }))
        : [],
      applicationType: consumerData?.applicationType ?? "",
      sourceOfPowerId: consumerData?.sourceOfPower?.id ?? "",
      dateOfConnection: consumerData?.dateOfConnection
        ? convertUTCDateToLocalDate(consumerData.dateOfConnection)
        : "",
      disconnectionDate: consumerData?.disconnectionDate
        ? convertUTCDateToLocalDate(consumerData.disconnectionDate)
        : "",
      electricityDuty: consumerData?.electricityDuty ?? "",
      securityDeposit: consumerData?.securityDeposit ?? "",
      applicant: {
        id: consumerData?.applicant?.id ?? 0,
        applicantTypeId: consumerData?.applicant?.applicantType?.id ?? "",
        applicantName: consumerData?.applicant?.applicantName ?? "",
        panNumber: consumerData?.applicant?.panNumber ?? "",
        gstNumber: consumerData?.applicant?.gstNumber ?? "",
        occupancyTypeId: consumerData?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered: consumerData?.applicant?.isGSTRegistered ?? "",
        addressType: consumerData?.applicant?.addressType ?? "",
        correspondenceAddress:
          consumerData?.applicant?.correspondenceAddress ?? "",
        city: consumerData?.applicant?.city ?? "",
        pinCode: consumerData?.applicant?.pinCode ?? "",
        email: consumerData?.applicant?.email ?? "",
        mobileNumber: consumerData?.applicant?.mobileNumber ?? "",
        landLine: consumerData?.applicant?.landLine ?? "",
        alternateEmail: consumerData?.applicant?.alternateEmail
          ? consumerData.applicant.alternateEmail.split(", ")
          : [],
        alternateMobileNumber:
          consumerData?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },

      totalBuildupArea: consumerData?.totalBuildupArea ?? "",
      totalNumberOfOccupants: consumerData?.totalNumberOfOccupants ?? "",
      dcsDemand: {
        id: consumerData?.dcsDemand?.id ?? 0,
        contractDemand: consumerData?.dcsDemand?.totalContractDemand ?? "",
        ultimateDemand: consumerData?.dcsDemand?.ultimateDemand ?? "",
        waterFlowRate: consumerData?.dcsDemand?.waterFlowRate ?? "",
        chilledWaterLineDiameter:
          consumerData?.dcsDemand?.chilledWaterLineDiameter ?? "",
      },
      waterSewageDemand: {
        id:
          consumerData?.waterDemand?.id ?? consumerData?.sewageDemand?.id ?? 0,
        waterDemand:
          consumerData?.waterDemand?.totalWaterDemand ??
          consumerData?.sewageDemand?.totalWaterDemand ??
          0,
        flushing:
          consumerData?.waterDemand?.totalFlushing ??
          consumerData?.sewageDemand?.totalFlushing ??
          0,
        domestic:
          consumerData?.waterDemand?.totalDomestic ??
          consumerData?.sewageDemand?.totalDomestic ??
          0,
        gardening:
          consumerData?.waterDemand?.totalGardening ??
          consumerData?.sewageDemand?.totalGardening ??
          0,
      },

      connectionLoad: {
        id: consumerData?.connectionLoad?.id ?? 0,
        backupRequired: !!consumerData?.connectionLoad?.backupkVA,
        backupkVA: consumerData?.connectionLoad?.backupkVA ?? "",
        loadDetails: consumerData?.connectionLoad?.loadDetail ?? "",
        supplyVoltage: consumerData?.connectionLoad?.supplyVoltage
          ? +consumerData?.connectionLoad?.supplyVoltage
          : "",
        supplyVoltageUnit:
          consumerData?.connectionLoad?.supplyVoltageUnit ?? "",
        powerRequirement:
          consumerData?.connectionLoad?.totalPowerRequirement ?? "",
        powerRequirementUnit:
          consumerData?.connectionLoad?.totalPowerRequirement <= 99 ? 1 : 2,
        timeShift: consumerData?.connectionLoad?.timeShiftEnum ?? "",
        tention: consumerData?.connectionLoad?.totalPowerRequirement
          ? consumerData.connectionLoad.totalPowerRequirement < 100
            ? "LT"
            : "HT"
          : "",

        isGreenPowerRequired: !!consumerData?.connectionLoad?.greenPower,
        greenPower: consumerData?.connectionLoad?.greenPower ?? "",
        backupPowerAlteration:
          consumerData?.connectionLoad?.backupPowerAlteration ?? "",
        decreaseOfLoad: consumerData?.connectionLoad?.decreaseOfLoad ?? "",
        expectedConnectionStartDate: consumerData?.connectionLoad
          ?.expectedConnectionStartDate
          ? convertUTCDateToLocalDate(
              consumerData.connectionLoad.expectedConnectionStartDate
            )
          : "",

        expectedConnectionEndDate: consumerData?.connectionLoad
          ?.expectedConnectionEndDate
          ? convertUTCDateToLocalDate(
              consumerData.connectionLoad.expectedConnectionEndDate
            )
          : "",

        increaseOfLoad: consumerData?.connectionLoad?.increaseOfLoad ?? "",
        totalPowerRequirement:
          consumerData?.connectionLoad?.totalPowerRequirement ?? "",
      },
      utilityAddress: {
        id: consumerData?.utilityAddress?.id ?? 0,
        premiseTypeId: consumerData?.utilityAddress?.premiseType?.id ?? "",
        premiseUsagePurpose:
          consumerData?.utilityAddress?.premiseUsagePurpose ?? "",
        premisesAreaId: consumerData?.utilityAddress?.premiseArea?.id ?? "",
        zoneName: consumerData?.utilityAddress?.zone?.name ?? "",
        otherZone: "",
        blockName: consumerData?.utilityAddress?.block?.name ?? "",
        otherBlock: "",
        roadName: consumerData?.utilityAddress?.road?.name ?? "",
        otherRoad: "",
        buildingName: consumerData?.utilityAddress?.building?.name ?? "",
        otherBuilding: "",
        floors: [],
        unitName: consumerData?.utilityAddress?.unitName ?? "",
        nearestLandmark: consumerData?.utilityAddress?.nearestLandmark ?? "",
        isBPL: consumerData?.utilityAddress?.isBPL ?? "",
        solarId: consumerData?.utilityAddress?.solarId ?? "",
      },
      extraValues: {
        applicantType: applicantTypes,
        premiseIdsForTimeShift: [],
        isRemarksRequired: false,
      },
    }),
    [consumerData, applicantTypes]
  );

  const isPowerUtilityConsumer = useMemo(
    () => utilityIds.includes(getOptionValue("utilityOptions", "Power")),
    [utilityIds]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: isPowerUtilityConsumer
      ? getUtilityConsumerValidation[currentStep]
      : getOtherUtilityConsumerValidation[currentStep],
    onSubmit: async () => {
      if (currentStep !== steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
    },
  });

  const {
    values,
    values: {
      connectionLoad: {
        backupkVA,
        powerRequirement,
        greenPower,
        loadDetails,
        supplyVoltage,
      },
      utilityAddress: { premiseTypeId },
      consumerNumber,
      electricityDuty,
      securityDeposit,
      extraValues: { isRemarksRequired },
    },
  } = formik;

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Consumer Details"),
        isCompleted: currentStep >= 1,
        component: (
          <UtilityConsumerDetails
            formik={formik}
            isPowerUtilityConsumer={isPowerUtilityConsumer}
          />
        ),
      },
      {
        id: 2,
        name: t("Applicant Details"),
        isCompleted: currentStep >= 2,
        component: <ApplicantDetails formik={formik} isEditable />,
      },

      {
        id: 3,
        name: t("Address for Power Connection"),
        isCompleted: currentStep >= 3,
        component: (
          <AddressForPower formik={formik} floorOptions={floorOptions} />
        ),
      },
      {
        id: 4,
        name: t(
          isPowerUtilityConsumer
            ? "Connection Load Details"
            : "Contract Demands"
        ),
        isCompleted: currentStep >= 4,
        component: isPowerUtilityConsumer ? (
          <ConnectionLoadDetails formik={formik} />
        ) : (
          <UtilitiesFields formik={formik} />
        ),
      },
    ],
    [currentStep, formik, isPowerUtilityConsumer]
  );

  const submitBtnHandler = async () => {
    if ((remarks || !isRemarksRequired) && currentStep === steps.length - 1) {
      const updatedValues = {
        ...values,
        applicant: {
          ...values.applicant,
          mobileNumber: String(values.applicant.mobileNumber) ?? "",
          alternateEmail: values.applicant.alternateEmail.length
            ? values.applicant.alternateEmail.join(", ")
            : "",
          cityId: values.applicant.city?.id ?? "",
        },
        remarks,
        consumerNumber: String(values.consumerNumber) ?? "",
        connectionLoad: {
          ...values.connectionLoad,
          expectedConnectionStartDate: values.connectionLoad
            .expectedConnectionStartDate
            ? values.connectionLoad.expectedConnectionStartDate.format(
                "YYYY-MM-DD"
              )
            : "",
          expectedConnectionEndDate: values.connectionLoad
            .expectedConnectionEndDate
            ? values.connectionLoad.expectedConnectionEndDate.format(
                "YYYY-MM-DD"
              )
            : "",
        },
        utilityAddress: {
          ...values.utilityAddress,
          zoneName:
            values.utilityAddress.zoneName === "other"
              ? values.utilityAddress.otherZone
              : values.utilityAddress.zoneName,
          blockName:
            values.utilityAddress.blockName === "other"
              ? values.utilityAddress.otherBlock
              : values.utilityAddress.blockName,
          roadName:
            values.utilityAddress.roadName === "other"
              ? values.utilityAddress.otherRoad
              : values.utilityAddress.roadName,
          buildingName:
            values.utilityAddress.buildingName === "other"
              ? values.utilityAddress.otherBuilding
              : values.utilityAddress.buildingName,
          floors: values.utilityAddress.floors.map(({ label, value }) => ({
            id: value,
            name: label,
          })),
        },
        dateOfConnection: values.dateOfConnection
          ? values.dateOfConnection.format("YYYY-MM-DD")
          : "",
        disconnectionDate: values.disconnectionDate
          ? values.disconnectionDate.format("YYYY-MM-DD")
          : "",
      };

      const payload = omit(updatedValues, [
        "extraValues",
        "applicationType",
        "applicant.city",
        "utilityAddress.otherZone",
        "utilityAddress.otherBlock",
        "utilityAddress.otherRoad",
        "utilityAddress.otherBuilding",
        "utilityId",
        "utilities",
        ...(values.utilityId
          ? keysToDeleteForOtherUtility
          : keysToDeleteForPowerUtility),
      ]);

      const finalPayload = Object.entries(filterEmptyStrings(payload)).reduce(
        (res, [key, value]) => {
          if (
            typeof value !== "object" ||
            value instanceof File ||
            Object.keys(value).length > 1
          ) {
            return { ...res, [key]: value };
          }
          return res;
        },
        {}
      );

      try {
        if (values.utilityId) {
          await addUtilityConsumer(finalPayload);
        } else {
          await updateOtherUtilityUtilityConsumer({
            ...finalPayload,
            utilities: values.utilities.map(({ value }) => value),
          });
        }
        handleClose();
      } catch (error) {
        console.error(error);
      }

      setRemarks("");
    } else {
      setIsError(true);
    }
  };

  useEffect(() => {
    if (powerConsumerData) {
      const currentConsumerData = {
        backupPower: powerConsumerData?.connectionLoad?.backupkVA ?? "",
        consumerNumber: powerConsumerData?.consumerNumber ?? "",
        contractLoad:
          powerConsumerData?.connectionLoad?.totalPowerRequirement ?? "",
        electricityDuty: powerConsumerData?.electricityDuty ?? "",
        greenPower: powerConsumerData?.connectionLoad?.greenPower ?? "",
        loadDetails: powerConsumerData?.connectionLoad?.loadDetail ?? "",
        premiseTypeId: powerConsumerData?.utilityAddress?.premiseType?.id ?? "",
        securityDeposit: powerConsumerData?.securityDeposit ?? "",
        supplyVoltage: powerConsumerData?.connectionLoad?.supplyVoltage
          ? +powerConsumerData.connectionLoad.supplyVoltage
          : "",
      };

      const updatedConsumerData = {
        backupPower: backupkVA,
        consumerNumber: consumerNumber,
        contractLoad: powerRequirement,
        electricityDuty: electricityDuty,
        greenPower: greenPower,
        loadDetails: loadDetails,
        premiseTypeId: premiseTypeId,
        securityDeposit: securityDeposit,
        supplyVoltage: supplyVoltage,
      };

      setFieldValue(
        "extraValues.isRemarksRequired",
        !isEqual(currentConsumerData, updatedConsumerData)
      );
    }
  }, [
    backupkVA,
    consumerNumber,
    powerRequirement,
    electricityDuty,
    greenPower,
    loadDetails,
    premiseTypeId,
    securityDeposit,
    supplyVoltage,
  ]);

  useEffect(() => {
    (async () => {
      if (isPowerUtilityConsumer) {
        await getUtilityConsumerDataByConsumerId(utilityConsumerId);
      } else {
        await getOtherUtilityConsumerDataByConsumerId(utilityConsumerId);
      }
    })();
  }, [utilityConsumerId, isPowerUtilityConsumer]);

  const {
    handleSubmit,
    values: {
      utilityAddress: { premiseType },
    },
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (premiseType) {
      const updatedPremiseIdsForTimeShift = [];
      premiseType.forEach((premise) => {
        if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
          updatedPremiseIdsForTimeShift.push(premise.id);
        }
      });
      setFieldValue(
        "extraValues.premiseIdsForTimeShift",
        updatedPremiseIdsForTimeShift
      );
    }
  }, [premiseType]);

  useEffect(() => {
    const initialFloors = consumerData?.utilityAddress?.floors || [];

    if (floorOptions.length && initialFloors.length) {
      setFieldValue(
        "utilityAddress.floors",
        floorOptions.filter((option) =>
          initialFloors.some((floor) => floor.id === option.value)
        )
      );
    }
  }, [floorOptions, consumerData]);

  if (isLoading || isOtherUtilityConsumerDataLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {iseRemarkModalOpen && (
        <Modal
          title="Add Remarks"
          isOpen
          handleClose={() => {
            setIsRemarkModalOpen(false);
            setIsError(false);
            setRemarks("");
          }}
          maxWidth="sm"
        >
          <>
            <TextField
              placeholder={t("Add Remarks") + "..."}
              className="mb-1 mt-2"
              multiline
              rows={3}
              error={isError}
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                setIsError(!e.target.value);
              }}
              fullWidth
              size="small"
            />
            <Typography
              variant="subtitle2"
              className={` ${
                isError ? "visible" : "invisible"
              } input-field-error-message`}
              sx={{ color: theme.palette.error.main }}
            >
              {t("Remarks is required")}
            </Typography>
            <div className="d-flex justify-content-end gap-1">
              <Button
                variant="contained"
                size="small"
                onClick={submitBtnHandler}
                startIcon={
                  isConsumerDataUpdating ? <CircularProgress size={20} /> : null
                }
                disabled={isConsumerDataUpdating}
              >
                {t("Submit")}
              </Button>
            </div>
          </>
        </Modal>
      )}
      <Modal
        isOpen
        handleClose={handleClose}
        title="Edit Consumer Details"
        maxWidth="lg"
      >
        <div className="stepper-container">
          <Stepper
            steps={steps}
            handleSubmit={async () => {
              if (
                isRemarksRequired &&
                currentStep === steps.length - 1 &&
                isPowerUtilityConsumer
              ) {
                setIsRemarkModalOpen(true);
              } else if (currentStep !== steps.length - 1) {
                handleSubmit();
              } else {
                await submitBtnHandler();
              }
            }}
            activeStepIndex={currentStep}
            setActiveStepIndex={setCurrentStep}
            isLoading={
              isConsumerDataUpdating || isOtherUtilityConsumerDataUpdating
            }
            isDraftDisabled
            formik={formik}
          />
        </div>
      </Modal>
    </>
  );
};

UtilityConsumerEditModal.propTypes = {
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
  utilityIds: PropTypes.array,
  t: PropTypes.func,
};

export default withTranslation()(UtilityConsumerEditModal);
